'use client';

import Link from 'next/link';
import { useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';

const plans = [
  {
    key: 'free',
    name: 'Free',
    monthlyPrice: 0,
    annualPrice: 0,
    description: 'For solo founders getting started',
    features: [
      '1 project, 1 portal, 50 tracked voters',
      'Kanban & List views',
      'Basic task management with dependencies',
      'Comments & @mentions',
      'Daily standups',
      'Project kickoff checklist',
      'Keyboard shortcuts',
      'Feature request voting',
      'Public roadmap',
      'Magic link auth',
      'Community support',
    ],
    cta: 'Get Started',
    ctaHref: '/register',
    highlighted: false,
  },
  {
    key: 'starter',
    name: 'Starter',
    monthlyPrice: 29,
    annualPrice: 19,
    extraUserPrice: 7,
    extraUserPriceAnnual: 5,
    extraUserThreshold: 3,
    description: 'For small teams shipping fast',
    features: [
      '3 projects, 3 team members, 500 voters',
      'All views (Kanban, List, Calendar)',
      'Custom fields',
      'File attachments',
      'Basic automations',
      'Feature prioritization scoring',
      'Risk register',
      'Project stakeholders & weekly status updates',
      'Recurring tasks',
      'Slack notifications',
      'Weekly email digest',
      'Custom domain, remove branding',
      'Email support',
    ],
    cta: 'Start Free Trial',
    ctaHref: '/register?plan=starter',
    highlighted: false,
  },
  {
    key: 'pro',
    name: 'Pro',
    monthlyPrice: 59,
    annualPrice: 49,
    extraUserPrice: 5,
    extraUserPriceAnnual: 4,
    extraUserThreshold: 10,
    description: 'For growing teams that need the full toolkit',
    features: [
      'Unlimited projects, 10 members, 3 portals, 5K voters',
      'All views incl. Gantt, Timeline, Scrum Board',
      'Sprint planning with burndown & velocity',
      'OKR tracking',
      'Time tracking & timesheets',
      'Baselines & variance tracking',
      'Everything in Starter (risks, stakeholders, updates, recurring tasks, Slack, digest)',
      'Unlimited custom fields & automations',
      'Release planning',
      'Composable dashboards',
      'Changelog & announcements',
      'Shared read-only views',
      'Full integrations (Zapier, webhooks)',
      'API access with OpenAPI docs',
      'Email support',
    ],
    cta: 'Start Free Trial',
    ctaHref: '/register?plan=pro',
    highlighted: true,
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    monthlyPrice: -1,
    annualPrice: -1,
    description: 'For organizations that need strategic planning, AI & SSO',
    features: [
      'Everything in Pro + unlimited members, portals & voters',
      'Portfolio management (cross-project oversight)',
      'Weekly sync meetings (scorecard, rocks, IDS)',
      'Cost tracking & project budgets',
      'Workload management & capacity planning',
      'AI features (command bar, task decomposition, risk analysis, smart writing)',
      'Digital whiteboard (real-time collaboration)',
      'SSO / SAML',
      'White-label portal',
      'Custom integrations',
      'Dedicated success manager',
      'SLA guarantee',
      'Custom contract',
    ],
    cta: 'Contact Sales',
    ctaHref: 'mailto:sales@planetroadmap.com',
    highlighted: false,
  },
];

export default function PricingPage() {
  const [annual, setAnnual] = useState(true);

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute top-0 right-1/3 w-48 sm:w-96 h-48 sm:h-96 bg-blue-500/20 rounded-full blur-3xl -translate-y-1/2" aria-hidden="true" />
        <div className="absolute bottom-0 left-1/4 w-48 sm:w-96 h-48 sm:h-96 bg-cyan-400/15 rounded-full blur-3xl translate-y-1/2" aria-hidden="true" />

        <div className="relative max-w-4xl mx-auto px-6 py-28 md:py-36 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-balance leading-[1.1]">
            Simple,{' '}
            <span className="text-blue-400">transparent pricing</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-slate-400 max-w-2xl mx-auto text-balance leading-relaxed">
            Start free, upgrade when you need to. Flat plans with generous seat caps — only pay per user when you outgrow them.
          </p>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-3 mb-12">
            <span className={`text-sm font-medium ${!annual ? 'text-foreground' : 'text-muted-foreground'}`}>
              Monthly
            </span>
            <button
              onClick={() => setAnnual(!annual)}
              role="switch"
              aria-checked={annual}
              aria-label="Bill annually"
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                annual ? 'bg-primary' : 'bg-muted-foreground/30'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  annual ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${annual ? 'text-foreground' : 'text-muted-foreground'}`}>
              Annual
            </span>
            {annual && (
              // Pro: $59/mo vs $49/mo annual = $120/yr saved on $708 list = ~17%.
              // Starter saves more (~34%). "Save 17%" is honest for both.
              <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 bg-primary/10 px-2 py-0.5 rounded-full">
                Save 17%
              </span>
            )}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto items-start">
            {plans.map((plan) => {
              const price = annual ? plan.annualPrice : plan.monthlyPrice;
              const isCustom = price === -1;

              return (
                <div
                  key={plan.key}
                  className={`relative rounded-2xl p-8 transition-all duration-200 ${
                    plan.highlighted
                      ? 'bg-white dark:bg-card ring-2 ring-primary shadow-xl scale-[1.02]'
                      : 'bg-white dark:bg-card border border-border/60 hover:-translate-y-1 hover:shadow-lg'
                  }`}
                >
                  {plan.highlighted && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="inline-block bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                  <div className="mb-1">
                    {isCustom ? (
                      <span className="text-4xl font-bold tracking-tight">Custom</span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold tracking-tight">${price}</span>
                        {price > 0 && (
                          <span className="text-muted-foreground text-sm">/mo</span>
                        )}
                      </>
                    )}
                  </div>
                  {!isCustom && price > 0 && annual && (
                    <p className="text-xs text-muted-foreground mb-1">
                      ${price * 12}/year, billed annually
                    </p>
                  )}
                  {plan.extraUserPrice && plan.extraUserThreshold && (
                    <p className="text-xs text-muted-foreground mb-1">
                      +${annual ? plan.extraUserPriceAnnual : plan.extraUserPrice}/user/mo after {plan.extraUserThreshold}
                    </p>
                  )}
                  <p className="text-muted-foreground text-sm mb-8">{plan.description}</p>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm">
                        <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    href={plan.ctaHref}
                    className={`block text-center py-3 rounded-lg font-semibold transition-all duration-200 ${
                      plan.highlighted
                        ? 'bg-primary text-primary-foreground shadow-sm hover:-translate-y-0.5 hover:shadow-md active:translate-y-0'
                        : 'border border-input hover:bg-accent'
                    }`}
                  >
                    {plan.cta}
                  </Link>
                </div>
              );
            })}
          </div>

          {/* FAQ */}
          <div className="mt-20 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-8">Frequently asked questions</h2>
            <div className="text-left space-y-6">
              <div>
                <h3 className="font-semibold mb-1">Can I switch plans at any time?</h3>
                <p className="text-sm text-muted-foreground">
                  Yes. Upgrade or downgrade whenever you want. When upgrading, you only pay the
                  prorated difference. When downgrading, your credit is applied to future invoices.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">What counts as a &quot;tracked voter&quot;?</h3>
                <p className="text-sm text-muted-foreground">
                  Anyone who signs in to your feedback portal to vote, comment, or submit a request.
                  Anonymous browsers don&apos;t count toward your limit.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">Is there a free trial?</h3>
                <p className="text-sm text-muted-foreground">
                  All paid plans come with a 14-day free trial. No credit card required to start.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">What happens if I hit a limit?</h3>
                <p className="text-sm text-muted-foreground">
                  You won&apos;t lose any data. We&apos;ll let you know you&apos;ve reached your limit and
                  prompt you to upgrade. Existing data stays intact.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">What are the AI features?</h3>
                <p className="text-sm text-muted-foreground">
                  The Enterprise plan includes AI-powered tools like a command bar for quick actions,
                  automatic task decomposition, risk analysis, and smart writing assistance.
                  These features help you plan faster and catch issues early.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] bg-blue-500/10 rounded-full blur-3xl" aria-hidden="true" />
        <div className="relative max-w-3xl mx-auto px-6 py-24 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-balance">
            Start building your roadmap today
          </h2>
          <p className="mt-4 text-lg text-slate-400 text-balance">
            Free to start. No credit card required.
          </p>
          <div className="mt-8">
            <Link
              href="/register"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-500 px-8 py-3.5 font-semibold shadow-sm hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 transition-all duration-200"
            >
              Get Started Free
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
